import * as React from 'react';
import { NS } from '@/common/namespaces';
import { BLAZOR_ASSEMBLY } from "@/common/constants";
import { blazorAlert, getXmpValueNS, trimQuotes } from '@/common/utilities';
import { SubscriberService, OnUpdateEvent, createStaticServiceInstance, createUseZenAutoArtService } from "./base-subscriber-service";
import { metadataServiceInstance } from './metadata-service';
import { zenDataServiceInstance } from './zen-data-service';

const dontAlertStatusCodes = [504];

const filteredAlert = async (resp: Response, msg: string) => {
    if (!dontAlertStatusCodes.includes(resp.status)) {
        await blazorAlert("Error", msg);
    }
}

export default class FontsService extends SubscriberService<FontsService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return fontsServiceInstance._RegisterComponent(comp, onUpdate);
    }

    public fonts: PanelDesigner.IFont[] = [];
    public browserLoadedFonts: string[] = [];
    public missingFonts: string[] = [];

    constructor() {
        super();

        window.addEventListener("zaa-editor-ready", () => {
            this.RefreshFonts();
        });

        window.addEventListener("svgcontent-changed", (ev) => {
            this.AddFontDependencies(ev.detail);
        });
    }
    
    public async RefreshFonts() {
        const response = await fetch(`/api/designer/Fonts/${zenDataServiceInstance.GetCustomerID()}`);

        if (!response.ok) {
            console.error(`Could not fetch fonts. A HTTP ${response.status} (${response.statusText}) error occured.`, response);
            await filteredAlert(response, `Error: Could not fetch fonts! A HTTP ${response.status} (${response.statusText}) error occured!`);
            return;
        }

        const fonts = await response.json() as PanelDesigner.IFont[];

        if (!fonts) {
            console.error("Could not fetch fonts. No data was returned from repsonse. Making another attempt in 2 seconds.", response);
            setTimeout(() => { this.RefreshFonts() }, 2000);
            return;
        }

        this.fonts = fonts;

        this.AddFontDependency("avenir");
    }

    public GetFontStylesForFontFamily(fontFamily: string): string[] {
        const ret = [];
        const ff = fontFamily.toLowerCase();

        for (const f of this.fonts) {
            if (f.name.toLowerCase() === ff) {
                for (const s of f.styles) {
                    ret.push(s);
                }
                break;
            }
        }

        if (ret.length == 0) {
            ret.push('$regular');
        }

        return ret;
    }

    /**
     * Adds any AutoArt fonts found in the supplied document to the browser's collection of loaded fonts.
     **/
    public AddFontDependencies(svgDocument: SVGSVGElement) {
        const neededFonts: string[] = [];

        const parseEl = (el: SVGElement) => {
            let fontFamily = el.getAttribute("pe-fontfamily");
            if (fontFamily) {
                const fontStyle = el.getAttribute("pe-fontstyle");

                if (fontStyle && fontStyle.toLowerCase() != "$regular") {
                    fontFamily += ` ${fontStyle}`;
                }
            } else {
                fontFamily = el.getAttribute("font-family");
            }

            if (fontFamily) {
                fontFamily = fontFamily.trim().toLowerCase();

                if (!neededFonts.includes(fontFamily)) {
                    neededFonts.push(fontFamily);
                }
            }

            for (const child of el.children) {
                parseEl(child as SVGElement);
            }
        }

        parseEl(svgDocument);
        for (const fnt of neededFonts) {
            this.AddFontDependency(fnt);
        }
    }

    /**
     * Adds the given font family to the browser's collection of loaded fonts if possible.
     **/
    public async AddFontDependency(fontFamily: string) {
        fontFamily = SanitizeFontFamily(fontFamily);

        this.loadRequiredFont(fontFamily, this.GetFontRequestUrl(fontFamily));
    }

    public GetFontRequestUrl(fontFamily: string) {
        return `/api/designer/font/${zenDataServiceInstance.GetCustomerID()}/${SanitizeFontFamily(fontFamily)}`;
    }

    public async loadRequiredFont(fontFamily: string, fontRequestUrl: string): Promise<string> {
        if (this.browserLoadedFonts.includes(fontFamily)) {
            return "exists";
        } else if(this.missingFonts.includes(fontFamily)) {
            return "fail";
        }

        try {
            if(FontsService.isFontAvailable(fontFamily)) {
                console.log(`Detected that font ${fontFamily} is already available in the browser.`);
                this.browserLoadedFonts.push(fontFamily);
                return "exists";
            }

            // Check if we have an alias.
            let fontAliases: string[] = [];
            for(const li of metadataServiceInstance.xmpAllFonts) {
                const fontName = SanitizeFontFamily(getXmpValueNS(li, NS.ST_FNT, "fontName", null));
                if(fontName !== fontFamily) {
                    continue;
                }

                const liFontFamily = getXmpValueNS(li, NS.ST_FNT, "fontFamily", null);
                const liFontFace = getXmpValueNS(li, NS.ST_FNT, "fontFace", null);
                const liFontFile = getXmpValueNS(li, NS.ST_FNT, "fontFileName", null);

                if(liFontFamily && fontFamily !== liFontFamily) {
                    fontAliases = this.getAllFontAliases(fontFamily, liFontFamily, liFontFace, liFontFile);
                   
                    for(const v of fontAliases) {
                        if(await this.tryAddFontAlias(fontFamily, v)) {
                            return "ok";
                        }
                    }

                    break;
                }
            }

            console.info(`Loading required font ${fontFamily}`);
            this.browserLoadedFonts.push(fontFamily);
            const font = new FontFace(fontFamily, `url('${fontRequestUrl}')`);
            try {
                await font.load();
                document.fonts.add(font);
                return "ok";
            } catch (ex) {
                console.error(ex);
                // We do an extra check here, because this is async and another call to this method may have already
                // failed to load the font. We only need to notify the user the first time.
                if(!this.missingFonts.includes(fontFamily)) {
                    this.missingFonts.push(fontFamily);
                    DotNet.invokeMethod(BLAZOR_ASSEMBLY, 'LogFontLoadError', fontFamily, fontAliases);
                }
                return "fail";
            }
        } finally {
            this._UpdateSubscribers();
        }
    }

    private getAllFontAliases(originalFontFamily: string, newFontFamily: string, newFontFace?: string, newFontFile?: string): string[] {
        newFontFamily = newFontFamily.trim();
        newFontFace = newFontFace?.trim() ?? "";
        newFontFile = newFontFile?.trim() ?? "";

        const aliases = [
            originalFontFamily,
        ];

        if(newFontFile.length > 0 && isNaN(newFontFile as unknown as number)) {
            // isNaN check is due to the fact Illustrator may sometimes give us an integer rather than
            // a file name for the <stFnt:fontFileName> XMP entry.
            aliases.push(newFontFile);
        }

        if(newFontFace.length > 0) {
            aliases.push(`${newFontFamily} ${newFontFace}`);
            aliases.push(`${newFontFamily}-${newFontFace}`);
            aliases.push(`${newFontFamily}_${newFontFace}`);
            aliases.push(`${newFontFamily}${newFontFace}`);
        }

        if(newFontFace.length === 0 || newFontFace.toLowerCase() === "regular") {
            aliases.push(newFontFamily);
        }

        return aliases;
    }

    private async tryAddFontAlias(originalFontFamily: string, alias: string): Promise<boolean> {
        if(!FontsService.isFontAvailable(alias)) {
            return false;
        }

        const font = new FontFace(originalFontFamily, `local('${alias}'), local('${originalFontFamily}')`);
        try {
            console.log(`Detected that font '${originalFontFamily}' can be aliased to '${alias}'.`);

            await font.load();
            document.fonts.add(font);
            this.browserLoadedFonts.push(originalFontFamily);
            this.browserLoadedFonts.push(alias);
            return true;
        } catch (ex) {
            console.error(ex);
        }

        return false;
    }

    // From https://www.samclarke.com/javascript-is-font-available/
    static readonly isFontAvailable = (function (document) {
        let width;
        let body = document.body;

        let container = document.createElement('span');
        container.innerHTML = Array(100).join('wi');
        container.style.cssText = [
        'position:absolute',
        'width:auto',
        'font-size:128px',
        'left:-99999px'
        ].join(' !important;');

        const getWidth = function (fontFamily) {
        container.style.fontFamily = fontFamily;

        body.appendChild(container);
        width = container.clientWidth;
        body.removeChild(container);

        return width;
        };

        // Pre compute the widths of monospace, serif & sans-serif
        // to improve performance.
        const monoWidth  = getWidth('monospace');
        const serifWidth = getWidth('serif');
        const sansWidth  = getWidth('sans-serif');

        return function (font) {
        return monoWidth !== getWidth(font + ',monospace') ||
            sansWidth !== getWidth(font + ',sans-serif') ||
            serifWidth !== getWidth(font + ',serif');
        };
    })(document);
}

export const fontsServiceInstance = createStaticServiceInstance(new FontsService(), "fontsService");

// For React Functional Components
export const useFontsService = createUseZenAutoArtService(fontsServiceInstance);

function SanitizeFontFamily(fontFamily: string | null) {
    if(!fontFamily || fontFamily.length === 0) {
        return fontFamily;
    }

    return trimQuotes(fontFamily.trim()).trim().toLowerCase();
}
